import { io, type Socket } from 'socket.io-client';
import { WsCommand, type WsEvent, type WsEventMap } from '@threadline/contracts';
import { api, getAccessToken, onTokenChange } from './api';

/**
 * One socket for the whole app.
 *
 * React Query owns the cache; the socket's only job is to push deltas into it,
 * so components subscribe to events rather than holding socket state. The
 * connection is created lazily on first login and torn down on logout.
 */

const URL = import.meta.env.VITE_SOCKET_URL ?? '';
let socket: Socket | null = null;

/** Sessions the UI currently wants live updates for; replayed on reconnect. */
const subscribedSessions = new Set<string>();

export type ConnectionState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'failed';

const stateListeners = new Set<(state: ConnectionState) => void>();
let currentState: ConnectionState = 'idle';

function setState(next: ConnectionState) {
  if (next === currentState) return;
  currentState = next;
  for (const listener of stateListeners) listener(next);
}

export function onConnectionState(listener: (state: ConnectionState) => void): () => void {
  stateListeners.add(listener);
  listener(currentState);
  return () => stateListeners.delete(listener);
}

export function getConnectionState(): ConnectionState {
  return currentState;
}

/**
 * The access token only lives for 15 minutes, and the gateway checks it at
 * handshake time. A reconnect after an API restart or a laptop sleep therefore
 * tends to present an expired token, and handing that over just gets the
 * connection rejected. Refreshing here keeps every handshake viable.
 */
function tokenIsFresh(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1] ?? '')) as { exp?: number };
    // A little slack so a token that dies mid-handshake counts as stale.
    return typeof payload.exp === 'number' && payload.exp * 1000 > Date.now() + 10_000;
  } catch {
    return false;
  }
}

async function handshakeToken(): Promise<string | null> {
  const current = getAccessToken();
  if (current && tokenIsFresh(current)) return current;
  return api.refresh();
}

export function connectSocket(): Socket {
  if (socket) return socket;

  setState('connecting');
  socket = io(`${URL}/ws`, {
    path: '/socket.io',
    transports: ['websocket'],
    auth: (cb) => void handshakeToken().then((token) => cb({ token })),
    reconnectionDelay: 500,
    reconnectionDelayMax: 8_000,
    // Unlimited: the console is meant to be left open on a wall display, and
    // giving up after N tries means someone has to notice and refresh.
    reconnectionAttempts: Infinity,
  });

  // Counted between successful authentications: `connect` fires before the
  // gateway has vetted the token, so only `ready` proves the credentials work.
  let rejections = 0;

  socket.on('connect', () => {
    setState('connected');
    for (const sessionId of subscribedSessions) {
      socket?.emit(WsCommand.SUBSCRIBE_SESSION, { sessionId });
    }
  });

  socket.on('ready', () => {
    rejections = 0;
  });

  socket.on('disconnect', (reason) => {
    if (reason !== 'io server disconnect') {
      setState('reconnecting');
      return;
    }

    // The server hung up on purpose - an expired or revoked token. socket.io
    // will not retry after this on its own, so refresh and reconnect here; a
    // session that fails repeatedly with fresh tokens is genuinely dead, and
    // looping on it would hammer the auth endpoint.
    rejections += 1;
    if (rejections >= 3) {
      setState('failed');
      return;
    }

    setState('reconnecting');
    void api.refresh().then((token) => {
      if (!socket) return;
      if (token) socket.connect();
      else setState('failed');
    });
  });

  socket.on('connect_error', () => setState('reconnecting'));

  // A rotated access token has to reach the handshake, which only reads it at
  // connect time.
  onTokenChange((token) => {
    if (!socket) return;
    if (!token) {
      disconnectSocket();
    } else if (!socket.connected) {
      socket.connect();
    }
  });

  return socket;
}

export function disconnectSocket(): void {
  socket?.removeAllListeners();
  socket?.disconnect();
  socket = null;
  subscribedSessions.clear();
  setState('idle');
}

export function on<E extends WsEvent>(
  event: E,
  handler: (payload: WsEventMap[E]) => void,
): () => void {
  const active = connectSocket();
  active.on(event as string, handler as (...args: unknown[]) => void);
  return () => {
    active.off(event as string, handler as (...args: unknown[]) => void);
  };
}

export function subscribeSession(sessionId: string): () => void {
  const active = connectSocket();
  subscribedSessions.add(sessionId);
  active.emit(WsCommand.SUBSCRIBE_SESSION, { sessionId });

  return () => {
    subscribedSessions.delete(sessionId);
    active.emit(WsCommand.UNSUBSCRIBE_SESSION, { sessionId });
  };
}

export function subscribeLogs(): void {
  connectSocket().emit(WsCommand.SUBSCRIBE_LOGS, {});
}
